import { useEffect, type ReactNode } from 'react';
import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import type { ControllerRenderProps } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { theme } from '@theguarantors/tg-package-material-ui';

import {
  GroupRadioButton,
  GroupRadioButtonSkeleton,
  type RadioInputBox,
} from '@/components/input/GroupRadioButton';
import { createDataTestId } from '@/utils/createDataTestId';

import { fieldHelperTextSx } from '../../components/field-helper.text';
import type { OptionBoxOptionsSource } from '../../config';
import type { OptionBoxOption } from '../../lease-details.types';
import { useResolvedOptions } from '../../hooks';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';

export type LeaseOptionBoxSelection = {
  /** The selected option's `value` (what lands in form state). */
  value: string;
  /** The selected option's `id` (distinguishes options that share a value). */
  optionId: string;
};

export type LeaseOptionBoxInputProps = {
  name: string;
  options: OptionBoxOptionsSource;
  /** Optional heading above the radios (e.g. "Rent Coverage"). */
  label?: ReactNode;
  /** Second form key that receives the selected option's `id`, if any. */
  companionIdField?: string;
  layout?: 'inline' | 'stacked';
  useDrawerStyles?: boolean;
  skeletonCount?: number;
  required?: boolean;
  disabled?: boolean;
  onSelect?: (selection: LeaseOptionBoxSelection) => void;
};

const toRadioInputBox = (option: OptionBoxOption): RadioInputBox => ({
  id: option.id,
  value: option.value,
  text: option.label,
  subText: option.subText,
  tooltip: option.tooltip,
  disabled: option.disabled,
  disabledTooltip: option.disabledTooltip,
});

// Card-style radio group bound to RHF via `useFormContext`. Mirrors how the
// legacy invite/CLD radios wired `GroupRadioButton` inside a `Controller`, so
// the rendered output is identical — only the binding moves to the engine.
export const LeaseOptionBoxInput = ({
  name,
  options: optionsSource,
  label,
  companionIdField,
  layout,
  useDrawerStyles,
  skeletonCount,
  required = false,
  disabled = false,
  onSelect,
}: LeaseOptionBoxInputProps) => {
  const { watch, setValue, trigger, getFieldState, formState } = useFormContext();
  const { options, isLoading, isError } = useResolvedOptions<OptionBoxOption>(optionsSource);
  const { asteriskColor, sectionSpacing } = useLeaseDetailsUI();
  const resolvedAsteriskColor = asteriskColor ?? theme.palette.red[600];
  const optionBoxSpacing = sectionSpacing?.optionBox ?? 1.5;

  const value = (watch(name) as string | undefined) ?? '';
  const selectedId = companionIdField ? (watch(companionIdField) as string | undefined) : undefined;
  const error = getFieldState(name, formState).error;

  // Mirrors legacy `screening.input.tsx`: when hook-backed options finish
  // loading and the stored form value isn't among them (e.g. rent changed and
  // the SMB tier list rebuilt, or the deal hydrated a value the new property's
  // rules don't include), clear it. Without this the user sees no card
  // selected but a value still in form state, and submits a tier that's not
  // on screen.
  useEffect(() => {
    if (isLoading || isError) return;
    if (!value) return;
    if (options.some((opt) => opt.value === value)) return;
    setValue(name, '', { shouldValidate: true });
    if (companionIdField) setValue(companionIdField, '', { shouldDirty: false });
  }, [isLoading, isError, value, options, name, companionIdField, setValue]);

  const dataTestId = createDataTestId({ inputString: name, suffix: 'option-box' });

  const labelNode = label != null && label !== '' && (
    <Typography
      variant="body2"
      sx={{
        color: theme.palette.neutralGrey[950],
        fontSize: '14px',
        fontWeight: 350,
        lineHeight: '22px',
        letterSpacing: '0',
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: resolvedAsteriskColor }}>
          {' '}
          *
        </Box>
      )}
    </Typography>
  );

  if (isLoading) {
    return (
      <Stack direction="column" spacing={optionBoxSpacing}>
        {labelNode}
        <GroupRadioButtonSkeleton
          count={skeletonCount ?? (options.length || 3)}
          dataTestId={`${name}-skeleton`}
        />
      </Stack>
    );
  }

  if (isError) {
    return (
      <FormHelperText error sx={fieldHelperTextSx} data-testid={`${dataTestId}-helper-text`}>
        Failed to load options. Please try again.
      </FormHelperText>
    );
  }

  const field = {
    name,
    value,
    ref: () => {},
    onBlur: () => trigger(name),
    onChange: (_event: unknown, selectedValue: string, selectedOptionId: string) => {
      setValue(name, selectedValue, { shouldValidate: true, shouldDirty: true });
      if (companionIdField) {
        setValue(companionIdField, selectedOptionId, { shouldDirty: true });
      }
      trigger(name);
      onSelect?.({ value: selectedValue, optionId: selectedOptionId });
    },
  } as unknown as ControllerRenderProps;

  return (
    <Stack direction="column" spacing={optionBoxSpacing}>
      {labelNode}
      <Stack direction="column" spacing={optionBoxSpacing}>
        <GroupRadioButton
          id={name}
          dataTestId={dataTestId}
          options={options.map(toRadioInputBox)}
          selectedId={selectedId}
          field={field}
          required={required}
          disabled={disabled}
          layout={layout}
          useDrawerStyles={useDrawerStyles}
          styles={{
            options: {
              spacing: optionBoxSpacing,
              height: 64,
            },
          }}
        />
        {error?.message && (
          <FormHelperText error sx={fieldHelperTextSx} data-testid={`${dataTestId}-helper-text`}>
            {error.message.toString()}
          </FormHelperText>
        )}
      </Stack>
    </Stack>
  );
};
